/**
 * @file useUserPreferences.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-16
 * @modified 2026-09-22
 *
 * Hook for fetching and saving user preferences (theme, wallpaper, context menu)
 */

import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { setGuidedPreference } from '../utils/guidedMode';

// Color definitions with dark and light theme variants.
// Avoiding red/amber/green families so the primary colour can't be confused with
// --color-error / --color-warning / --color-success semantic system colours.
export const COLOR_OPTIONS = {
  aqua:     { label: 'Aqua',     dark: { hex: '#1eb4ff', rgb: '30, 180, 255' },  light: { hex: '#0095d0', rgb: '0, 149, 208' } },
  sky:      { label: 'Sky',      dark: { hex: '#38bdf8', rgb: '56, 189, 248' },  light: { hex: '#0284c7', rgb: '2, 132, 199' } },
  blue:     { label: 'Blue',     dark: { hex: '#5b8def', rgb: '91, 141, 239' },  light: { hex: '#2c5cc5', rgb: '44, 92, 197' } },
  indigo:   { label: 'Indigo',   dark: { hex: '#818cf8', rgb: '129, 140, 248' }, light: { hex: '#4338ca', rgb: '67, 56, 202' } },
  purple:   { label: 'Purple',   dark: { hex: '#a78bfa', rgb: '167, 139, 250' }, light: { hex: '#7c3aed', rgb: '124, 58, 237' } },
  lavender: { label: 'Lavender', dark: { hex: '#c4b5fd', rgb: '196, 181, 253' }, light: { hex: '#8b5cf6', rgb: '139, 92, 246' } },
  fuchsia:  { label: 'Fuchsia',  dark: { hex: '#e879f9', rgb: '232, 121, 249' }, light: { hex: '#a21caf', rgb: '162, 28, 175' } },
  pink:     { label: 'Pink',     dark: { hex: '#f472b6', rgb: '244, 114, 182' }, light: { hex: '#db2777', rgb: '219, 39, 119' } },
  teal:     { label: 'Teal',     dark: { hex: '#2dd4bf', rgb: '45, 212, 191' },  light: { hex: '#0d9488', rgb: '13, 148, 136' } },
  slate:    { label: 'Slate',    dark: { hex: '#94a3b8', rgb: '148, 163, 184' }, light: { hex: '#475569', rgb: '71, 85, 105' } },
};

export const WALLPAPER_OPTIONS = [
  { file: '4046.jpg', label: 'Mountains' },
  { file: '4047.jpg', label: 'Forest' },
  { file: '4049.jpg', label: 'Horizon' },
  { file: '6315.jpg', label: 'Dusk' },
  { file: '16289.jpg', label: 'Valley' },
  { file: '19135.jpg', label: 'Peaks' },
  { file: 'AdobeStock_289576562.jpeg', label: 'Meadow' },
  { file: 'AdobeStock_391814017.jpeg', label: 'Pasture' },
  { file: 'shutterstock_229284793.jpg', label: 'Countryside' },
  { file: 'shutterstock_488577520.jpg', label: 'Harvest' },
  { file: 'shutterstock_619834469.jpg', label: 'Farmland' },
  { file: 'shutterstock_1125147572.jpg', label: 'Fieldscape' },
  { file: 'shutterstock_1801398013.jpg', label: 'Hillside' },
  { file: 'shutterstock_1964664088.jpg', label: 'Prairie' },
  { file: 'shutterstock_1977940244.jpg', label: 'Rangeland' },
  { file: 'shutterstock_2139649467.jpg', label: 'Greenfield' },
  { file: 'shutterstock_2223584821.jpg', label: 'Moorland' },
  { file: 'shutterstock_2225993155.jpg', label: 'Paddock' },
  { file: 'shutterstock_2418263527.jpg', label: 'Lowland' },
  { file: 'photo-1491895200222-0fc4a4c35e18.jpg', label: 'Abstract 1' },
  { file: 'photo-1517241034903-9a4c3ab12f00.jpg', label: 'Abstract 2' },
  { file: 'photo-1533135091724-62cc5402aa20.jpg', label: 'Abstract 3' },
  { file: 'photo-1558591710-4b4a1ae0f04d.jpg', label: 'Abstract 4' },
  { file: 'photo-1620121692029-d088224ddc74.jpg', label: 'Abstract 5' },
  { file: 'photo-1654198340681-a2e0fc449f1b.jpg', label: 'Abstract 6' },
  { file: 'photo-1669295384050-a1d4357bd1d7.jpg', label: 'Abstract 7' },
  { file: 'photo-1672009190560-12e7bade8d09.jpg', label: 'Abstract 8' },
  { file: 'photo-1688494930045-328d0f95efe9.jpg', label: 'Abstract 9' },
  { file: 'photo-1718844054447-42759fd577e5.jpg', label: 'Abstract 10' },
  { file: 'Sheep.jpg', label: 'Flock' },
  { file: 'Morning.jpg', label: 'Excel Lorry' },
  { file: 'Sunset.jpg', label: 'EA Sunset' },
  { file: 'none', label: 'None' },
];

const DEFAULT_PREFERENCES = {
  theme_color: 'aqua',
  wallpaper: '4046.jpg',
  overlay_color: 'dark',
  overlay_opacity: 0.2,
  context_menu_items: null,
  favourites_items: null,
  // Show every slide-out label and nav tooltip without the wait. Off by
  // default — the delays exist for a reason. See utils/guidedMode.js.
  guided_mode: false
};

/**
 * Apply theme color to CSS custom properties
 */
export function applyThemeColor(colorKey) {
  const color = COLOR_OPTIONS[colorKey];
  if (!color) return;

  const theme = document.documentElement.getAttribute('data-theme') || 'dark';
  const variant = theme === 'light' ? color.light : color.dark;

  document.documentElement.style.setProperty('--color-primary', variant.hex);
  document.documentElement.style.setProperty('--color-primary-rgb', variant.rgb);
}

/**
 * Apply overlay color and opacity to content panel
 */
export function applyOverlay(overlayColor, overlayOpacity) {
  const root = document.documentElement;
  const rgb = overlayColor === 'light' ? '255, 255, 255' : '0, 0, 0';
  root.style.setProperty('--overlay-color', rgb);
  root.style.setProperty('--overlay-opacity', String(overlayOpacity));
}

/**
 * Apply wallpaper to dashboard container (behind all glass panels)
 */
export function applyWallpaper(wallpaperFile) {
  const container = document.querySelector('.dashboard-container');
  if (!container) return;

  if (!wallpaperFile || wallpaperFile === 'none') {
    container.style.backgroundImage = 'none';
  } else {
    container.style.backgroundImage = `url(/wallpapers/${wallpaperFile})`;
  }
}

function useUserPreferences() {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Apply defaults immediately so wallpaper/overlay show before API responds
    applyWallpaper(DEFAULT_PREFERENCES.wallpaper);
    applyOverlay(DEFAULT_PREFERENCES.overlay_color, DEFAULT_PREFERENCES.overlay_opacity);
    fetchPreferences();
  }, []);

  // Re-apply color when theme toggles (dark/light)
  useEffect(() => {
    const observer = new MutationObserver(() => {
      applyThemeColor(preferences.theme_color);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    return () => observer.disconnect();
  }, [preferences.theme_color]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/preferences`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const prefs = {
          theme_color: data.theme_color || DEFAULT_PREFERENCES.theme_color,
          wallpaper: data.wallpaper || DEFAULT_PREFERENCES.wallpaper,
          overlay_color: data.overlay_color || DEFAULT_PREFERENCES.overlay_color,
          overlay_opacity: data.overlay_opacity ?? DEFAULT_PREFERENCES.overlay_opacity,
          context_menu_items: data.context_menu_items || null,
          favourites_items: data.favourites_items || null,
          guided_mode: !!data.guided_mode
        };
        setPreferences(prefs);
        setGuidedPreference(prefs.guided_mode);
        applyThemeColor(prefs.theme_color);
        applyWallpaper(prefs.wallpaper);
        applyOverlay(prefs.overlay_color, prefs.overlay_opacity);
      }
    } catch (err) {
      console.error('Error fetching user preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = useCallback(async (newPrefs) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newPrefs)
      });

      if (response.ok) {
        const data = await response.json();
        const saved = data.preferences;
        setPreferences({
          theme_color: saved.theme_color,
          wallpaper: saved.wallpaper,
          overlay_color: saved.overlay_color || DEFAULT_PREFERENCES.overlay_color,
          overlay_opacity: saved.overlay_opacity ?? DEFAULT_PREFERENCES.overlay_opacity,
          context_menu_items: saved.context_menu_items,
          favourites_items: saved.favourites_items || null,
          guided_mode: !!saved.guided_mode
        });
        setGuidedPreference(!!saved.guided_mode);
        applyThemeColor(saved.theme_color);
        applyWallpaper(saved.wallpaper);
        applyOverlay(saved.overlay_color || DEFAULT_PREFERENCES.overlay_color, saved.overlay_opacity ?? DEFAULT_PREFERENCES.overlay_opacity);
        return { success: true };
      } else {
        throw new Error('Failed to save preferences');
      }
    } catch (err) {
      console.error('Error saving preferences:', err);
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    preferences,
    loading,
    saving,
    savePreferences,
    refetch: fetchPreferences
  };
}

export default useUserPreferences;
